<script>
import { SIDEBAR_CHAT } from '@modules/aawz-intelligence-v2/constants/TextChat'
import {
  indexarRotulosPorPersona,
  limitarRecentes,
} from '@modules/aawz-intelligence-v2/utils/agrupar-conversas'
import ConversationRow from '@modules/aawz-intelligence-v2/components/conversation-row/conversation-row.vue'

// O teto e a fatia moram em `limitarRecentes` (`utils/agrupar-conversas`), que
// veio do #1961 no merge: era a mesma regra que este componente fazia inline, e
// a convenção do repo é helper puro em `utils/` — testável sem montar Vue.

/**
 * Lista plana de conversas recentes, com o "Ver todas (N)" / "Ver menos".
 *
 * ★ `N` é o total CARREGADO (o `limit: 30` do GET /conversations), NÃO o total
 * do banco — a API não devolve contagem (gap G8). O rótulo diz "Ver todas" dos
 * carregados e em nenhum momento afirma ser o total.
 *
 * Não lê o store: recebe a lista já filtrada pela busca da sidebar, para
 * continuar montável sem store no teste.
 *
 * ★ As ações de PASTA saem do menu da linha aqui (`sem-acoes-de-pasta`): esta é
 * uma lista plana por DATA, e "Mover para pasta" / "Remover da pasta" operam
 * sobre um agrupamento que esta seção não mostra. Quem gerencia pasta é o modo
 * "Agrupar por" (`chat-sidebar-groups`), onde a prop não é passada.
 */
export default {
  name: 'IntelligenceV2ChatSidebarRecents',
  components: { ConversationRow },
  props: {
    conversas: {
      type: Array,
      default: function () {
        return []
      },
    },
    pastas: {
      type: Array,
      default: function () {
        return []
      },
    },
    // Catálogo dos agentes visíveis — usado só para traduzir `context` em
    // rótulo legível.
    agentes: {
      type: Array,
      default: function () {
        return []
      },
    },
    selectedId: {
      type: [String, Number],
      default: null,
    },
    // Repasse do modo limpo da lateral imersiva para a linha — ver
    // `estiloLimpo` em `conversation-row`. Aqui não muda nada; a lista continua
    // a mesma, só a linha se desenha diferente.
    estiloLimpo: {
      type: Boolean,
      default: false,
    },
    // Repasse puro pra `conversation-row` — ver os dois lá. Quem decide os
    // ids e o opt-in é `chat-sidebar.vue`; esta lista só encaminha.
    selectedIds: {
      type: Array,
      default: function () {
        return []
      },
    },
    permitirSelecaoLote: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      verTodas: false,
    }
  },
  computed: {
    // `includes` por linha a cada render era O(n·m) na lista de rolagem infinita.
    selecionadosEmLote() {
      return new Set(this.selectedIds)
    },
    // `context` guarda a PERSONA; o rótulo que o usuário reconhece é o dela.
    // Índice montado uma vez, e não uma busca por linha renderizada. Mesmo
    // helper que o modo Pastas usa — um dono só para "persona → rótulo".
    rotuloPorPersona() {
      return indexarRotulosPorPersona(this.agentes)
    },
    // ★ No modo limpo não há teto nem "Ver todas": a lateral imersiva ROLA, e
    // o que estiver carregado aparece — quem busca uma conversa antiga desce a
    // coluna em vez de descobrir um botão. `expandido = true` reusa o mesmo
    // helper em vez de um segundo caminho de fatiamento.
    recentes() {
      return limitarRecentes(this.conversas, this.verTodas || this.estiloLimpo)
    },
    total() {
      return this.recentes.total
    },
    // O rótulo secundário aqui é o AGENTE — justamente o que a lista plana
    // esconde. No modo AGENTE da visão agrupada o cabeçalho já diz e repetir
    // seria ruído; no modo PASTAS o cabeçalho é o nome da pasta, e lá a linha
    // também recebe o rótulo (ver `chat-sidebar-groups.vue`).
    itens() {
      const indice = this.rotuloPorPersona
      return this.recentes.visiveis.map(function (conversa) {
        return { conversa: conversa, rotulo: indice[conversa.context] || '' }
      })
    },
    mostraVerTodas() {
      if (this.estiloLimpo) return false
      return this.recentes.temMais
    },
    rotuloVerTodas() {
      if (this.verTodas) return SIDEBAR_CHAT.verMenos
      return SIDEBAR_CHAT.verTodas(this.total)
    },
  },
  methods: {
    alternarVerTodas() {
      this.verTodas = !this.verTodas
    },
  },
}
</script>

<template>
  <div class="chat-sidebar-recents">
    <!-- ★ As linhas entram em fade (14/09/2026): a página da rolagem infinita
         chegava de uma vez, 30 linhas num quadro. Só opacidade — a caixa já
         está lá quando o fade começa, então nada desloca o que se lê. Sem
         `appear`: a primeira pintura troca o esqueleto pela lista no lugar, e
         animar ali seria um segundo estado de carga. -->
    <transition-group
      tag="div"
      name="chat-sidebar-recents-linha"
      class="chat-sidebar-recents__lista"
    >
      <ConversationRow
        v-for="item in itens"
        :key="item.conversa.id"
        :conversation="item.conversa"
        :selected="item.conversa.id === selectedId"
        :folders="pastas"
        :rotulo-secundario="item.rotulo"
        :estilo-limpo="estiloLimpo"
        :permitir-selecao-lote="permitirSelecaoLote"
        :selected-for-batch="selecionadosEmLote.has(item.conversa.id)"
        sem-acoes-de-pasta
        @select="$emit('select', $event)"
        @rename="$emit('rename', $event)"
        @request-delete="$emit('request-delete', $event)"
        @move="$emit('move', $event)"
        @toggle-select="$emit('toggle-select', $event)"
      />
    </transition-group>
    <button
      v-if="mostraVerTodas"
      type="button"
      class="chat-sidebar-recents__ver-todas ia-sidebar-aux"
      @click="alternarVerTodas"
    >
      {{ rotuloVerTodas }}
    </button>
  </div>
</template>

<style lang="sass" scoped>
// Entrada das linhas — só opacidade (nada de `transform`: deslocar a linha
// moveria o que o usuário está lendo). Duração e curva dos tokens de motion
// do DS. Não há `-leave`: a linha apagada sai no mesmo quadro, como sempre.
.chat-sidebar-recents-linha-enter-active
  transition: opacity var(--motion-duration-default) var(--motion-easing-standard)

.chat-sidebar-recents-linha-enter
  opacity: 0

@media (prefers-reduced-motion: reduce)
  .chat-sidebar-recents-linha-enter-active
    transition: none

// Rótulo de ação, não parágrafo: o sublinhado o distingue do texto auxiliar de
// quem empresta a cor.
.chat-sidebar-recents__ver-todas
  padding: 6px 8px
  border: 0
  background: none
  text-decoration: underline
  cursor: pointer
</style>
